import React from 'react';
import { WeezHeader, Button, FilterSetController, Icon, Table, Dropdown, ContentCard, Card, CopyToClipboardLink } from '@weezevent/nacre';
import LoadingTable from '../../../../components/loadingTable';
import Category from '../../../../models/category';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DeleteCategoryModal from './deleteCategoryModal';
import css from '../../chart-list.module.css';

export default function Categories({ organization, chartId }) {
    const [inputSearchValue, setInputSearchValue] = React.useState('');
    const [categoryDeleted, setCategoryDeleted] = React.useState(null);

    const { t } = useTranslation();
    const history = useHistory();

    const [categories, loading, sync] = Category.useApiModel(
        {
            organizationId: organization.id,
            chartId,
            cache: false,
            allow_cache: false,
            query: {
                search: inputSearchValue
            }
        },
        [organization, chartId, inputSearchValue]
    );

    const handleSearch = React.useCallback((synth, value) => {
        setInputSearchValue(value);
    }, []);

    const handleNew = React.useCallback(() => {
        history.push(`/ticket/O${organization.id}/charts/${chartId}/categories/new`);
    }, [history, organization, chartId]);

    const categoriesCount = React.useMemo(() => {
        return categories?.length;
    }, [categories]);

    const onCloseDeleteModal = React.useCallback(() => {
        setCategoryDeleted(null);
    }, []);

    return (
        <>
            <WeezHeader title={t('ticket.charts.chart.categories')} rightComponent={<Button primary label={t('ticket.charts.chart.categories_add')} onClick={handleNew} />} />
            <Card>
                <ContentCard>
                    <FilterSetController
                        labelTotal={t('ticket.charts.chart.categories', { count: categoriesCount })}
                        onChange={() => {}}
                        search={{
                            onChange: (synth, value) => {
                                handleSearch(synth, value);
                            },
                            handleReset: () => {
                                handleSearch('');
                            },
                            placeholder: t('ticket.charts.chart.searchCategory'),
                            value: inputSearchValue
                        }}
                        total={categoriesCount}
                    />
                </ContentCard>
                <DeleteCategoryModal category={categoryDeleted} sync={sync} organization={organization} onClose={onCloseDeleteModal} chartId={chartId} />
                <Table useNew className={css['list__table']}>
                    <Table.THead>
                        <Table.Tr isHeader className={css['tr-header']}>
                            <th className={css['th-name']}>{t('attendee.info.category')}</th>
                            <th>{t('ticket.common.color')}</th>
                            <th>{t('ticket.common.type')}</th>
                            <th>{t('ticket.manager.tableHeader.actions')}</th>
                        </Table.Tr>
                    </Table.THead>
                    <Table.TBody>
                        {categories?.length > 0 &&
                            categories?.map(category => (
                                <CategoryRow key={category.uuid} chartId={chartId} organization={organization} category={category} onCategoryDeleted={setCategoryDeleted} />
                            ))}
                    </Table.TBody>
                </Table>
                <LoadingTable loading={loading} />
                {!loading && !categories?.length && (
                    <Card.EmptyState
                        icon={<Icon name="list" />}
                        title={inputSearchValue ? t('ticket.charts.chart.empty-search') : t('ticket.charts.chart.empty')}
                        labelButton={inputSearchValue ? null : t('ticket.charts.chart.categories_add')}
                        onClick={handleNew}
                    />
                )}
            </Card>
        </>
    );
}

export const CategoryRow = React.memo(({ chartId, category, organization, onCategoryDeleted }) => {
    const history = useHistory();
    const { t } = useTranslation();

    return (
        <Table.Tr
            className={css['tr-body']}
            style={{
                boxShadow: `inset 8px 0px 0px ${category.color}`
            }}
            onClick={() => history.push(`/ticket/O${organization.id}/charts/${chartId}/categories/${category.uuid}`)}
        >
            <Table.Td className={css['td-name']}>
                <span>{category.name}</span>
            </Table.Td>
            <Table.Td>
                <div className={css['td-copy']} onClick={e => e.stopPropagation()}>
                    <CopyToClipboardLink delay={1000} tooltipMini value={category.color} tooltip={t('ticket.common.action.copy')}>
                        {category.color}
                    </CopyToClipboardLink>
                </div>
            </Table.Td>
            <Table.Td>
                <span>{category.general_admission ? t('ticket.common.seating.freeSeating') : t('ticket.common.seating.numberedSeating')}</span>
            </Table.Td>
            <Table.CellDropdown>
                <Dropdown.Item item={t('ticket.common.action.edit')} onClick={() => history.push(`/ticket/O${organization.id}/charts/${chartId}/categories/${category.uuid}`)} />
                <Dropdown.Line />
                <Dropdown.Item deleteItem item={t('ticket.common.action.delete')} onClick={() => onCategoryDeleted(category)} />
            </Table.CellDropdown>
        </Table.Tr>
    );
});
